import { createRef, Component } from "react";
import { webix } from "extensions/webix";

import { store } from "common/store";
import { references } from "common/helpers/references";
import { getters } from "common/components/getters/getters";
import { showExportDialog } from "merchant/features/downloads/exportDialog/exportDialog";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowToBottom } from "@fortawesome/pro-regular-svg-icons";

import { Webix } from "./webix";
import { events, datatableEvents, processDataForPivotTable } from "../pivot-utils";

export class PivotTable extends Component {
    static defaultProps = {
        autoAdjustColumns: false,
        chart: false,
        "export": false,
        getters: true,
        permissions: true,
        eventHandler: () => {}
    };
    
    constructor(props) {
        super(props);
        this.childElement = createRef();
    }
    
    getValueFieldFormats() {
        if (this.props.getters) {
            // TODO: add currency_attribute property for this getters server-side
            const purchase_currency_amounts = new Set([
                "5af0f36edcfa183102e8df4e", // refunds_purchase_amount
                "5af0f36edcfa183102e8df3a", // sales_purchase_amount
                "5be1e41517b044396fdd8e9b" // fraud_purchase_amount
            ]);
            
            return this.props.fields.map((m) => {
                const getter = getters.findById(m);
                return {
                    name: getter.key,
                    valueType: getter.valueType,
                    className: getter.valueType,
                    weighted: getter.weighted,
                    original_currency: getter.original_currency,
                    currency_attribute: purchase_currency_amounts.has(m) ? "purchase_currency" : getter.currency_attribute,
                    func: getter.func
                };
            });
        }
        
        return this.props.fields;
    }
    
    exportTable = ({ dialogTitle, filename }) => {
        const tableChildren = this.childElement.current.ui.getChildViews();
        
        if (tableChildren.length !== 2) {
            throw new Error("Cannot export from this pivot table");
        }
    
        showExportDialog({
            dialogTitle: dialogTitle || "Export",
            filename: filename || this.props.exportDefaultName,
            formats: ["xlsx"],
            activeFormat: "xlsx",
            resources: [{
                value: "html",
                label: "HTML"
            }],
            activeResource: "html",
            content: [{ value: webix.toHTML(tableChildren[1]).outerHTML }]
        });
    };
    
    render() {
        const currency = store.getState().common.currency;
        
        const fieldMap = this.props.ui.fieldMap || {
            ...this.props.pivots,
            ...this.props.fields.reduce((memo, g) => {
                const getter = getters.findById(g);
                const normalizedName = (getter.name || "").replace("(normalized)", `(in ${currency})`);
                return {
                    ...memo,
                    [getter.key]: normalizedName || getter.name,
                    [`internal_change_${getter.key}`]: `${normalizedName} - change`,
                    [`pct_change_${getter.key}`]: `${normalizedName} - change (%)`,
                    [`abs_change_${getter.key}`]: `${normalizedName} - change (Abs)`
                };
            }, Object.create(null))
        };
        
        const ui = {
            ...this.props.ui,
            fieldMap,
            separateLabel: true,
            readonly: this.props.ui.readonly || (this.props.permissions && !store.getState().permissions.user_permissions.feature_tables_multi_pivots),
            spans: true,
            view: "pivot",
            availablePivots: Object.keys(this.props.pivots),
            isCustom: this.props.isCustom,
            datatable: {
                hover: "webix_row_hover",
                autoheight: true, // Autosize height based on content
                scrollAlignY: false, // https://docs.webix.com/api__ui.datatable_scrollaligny_config.html
                leftSplit: this.props.checkbox || this.props.recon3actions ? 2 : 1, // Freeze both columns if checkbox is requested
                select: false, // Do not allow row selection
                spans: true,
                type: {
                    folder: () => "" // Do not display icons for branches and leaves
                },
                onClick: {
                    flagged: (e, obj, objNode) => {
                        this.props.eventHandler({
                            type: "CHANGE_FLAGGED",
                            data: { obj, webix: this.childElement.current?.ui }
                        });
                    },
                    reconciled: (e, obj, objNode) => {
                        this.props.eventHandler({
                            type: "CHANGE_RECONCILED",
                            data: { obj, webix: this.childElement.current?.ui }
                        });
                    }
                },
                on: {
                    onHeaderClick: datatableEvents.onHeaderClick({
                        checkbox: this.props.checkbox,
                        recon3actions: this.props.recon3actions
                    }),
                    onCheck: datatableEvents.onCheck({
                        callback: (selectedRows) => {
                            this.props.eventHandler({
                                type: "CHECKBOX_CHANGE",
                                data: selectedRows
                            });
                        }
                    }),
                    onItemClick: datatableEvents.onItemClick({
                        checkbox: this.props.checkbox,
                        callback: (selectedRows) => {
                            this.props.eventHandler({
                                type: "CHECKBOX_CHANGE",
                                data: selectedRows
                            });
                        }
                    }),
                    onAfterRender: datatableEvents.onAfterRender(),
                    onAfterLoad: datatableEvents.onAfterLoad()
                },
                ...this.props.ui.datatable
            },
            max: false,
            min: false,
            popup: {
                position: "center",
                width: 960,
                height: Math.min(700, window.innerHeight),
                fieldsColumnWidth: 300,
                on: {
                    onViewInit: events.popup_onViewInit({
                        fieldMap,
                        pivots: this.props.pivots
                    })
                },
                ...this.props.ui?.popup
            },
            
            headerTemplate(config) {
                return this.config.fieldMap[config.text || config.name] || config.text; // Do not include operation name in column headers
            },
            
            columnWidth: 200, // Default width of data columns
            on: {
                onViewInit(name, config) {
                    if (name === "filters" && config.elements.length) {
                        config.elements.forEach((picker) => {
                            Object.assign(picker.cols[2], {
                                options: {
                                    ...picker.cols[2].options,
                                    data: picker.cols[2].options.data.map((item) => ({
                                        ...item,
                                        value: references.toAlias(item.value, picker.cols[2].field)
                                    }))
                                }
                            });
                        });
                    }
                },
                
                onBeforeRender: events.onBeforeRenderPivotTable({
                    autoAdjustColumns: this.props.autoAdjustColumns,
                    fieldFormat: this.getValueFieldFormats(),
                    initialSorting: this.props.initialSorting,
                    checkbox: this.props.checkbox,
                    recon3actions: this.props.recon3actions,
                    chart: this.props.chart,
                    calculated_fields: this.props.calculated_fields,
                    columnMapIteratee: this.props.columnMapIteratee
                }),
                onPopup: events.onPopup({
                    fieldMap,
                    onChangeFields: (obj) => {
                        this.props.eventHandler({
                            type: "FIELDS_CHANGED",
                            data: obj
                        });
                    },
                    onApplyPopup: (obj) => {
                        this.props.eventHandler({
                            type: "POPUP_APPLIED",
                            data: obj
                        });
                    },
                    isCustom: this.props.isCustom,
                    pazienPivot: this.props.pazienPivot
                }),
                onAfterLoad: events.onAfterLoad({
                    fieldFormat: this.getValueFieldFormats(),
                    initialSorting: this.props.initialSorting
                }),
                onItemClick: events.onItemClick({
                    checkbox: this.props.checkbox,
                    callback: (filters, id, pivot) => {
                        this.props.eventHandler({
                            type: "DRILLDOWN",
                            data: { filters, id, pivot }
                        });
                    }
                }),
                ...this.props?.ui?.on
            }
        };
        
        if (this.props.chart && typeof ui.contentTypes === "undefined") {
            ui.contentTypes = {};
        }
        
        const data = processDataForPivotTable({
            data: this.props.data,
            rows: ui.structure.rows,
            dates: ui.dates,
            baseDate: ui.baseDate,
            product: ui.product,
            values: this.props.getters ? this.props.fields.map((m) => getters.findById(m)) : this.props.fields
        });
        
        return (
            <>
                {this.props.export && (
                    <div className="pivot-table-toolbar">
                        <div className="button tooltipped tooltipped-nw" aria-label="Export table" onClick={this.exportTable}>
                            <FontAwesomeIcon icon={faArrowToBottom}/>
                        </div>
                    </div>
                )}
                <Webix className={this.props.className} ui={ui} data={data} ref={this.childElement}/>
            </>
        );
    }
}
